<template>
  <div class="flex flex-col" :style="{ height: 'calc(100vh - ' + headerHeight + 'px)' }">
    <FilterBar
      :labels="labels.property_search"
      :current-type="search.filters.type"
      :keyword="search.filters.keyword"
      :active-filter-count="activeFilterCount"
      :active-filters="activeFilterPills"
      @update:type="onFilterChange({ type: $event })"
      @update:keyword="onFilterChange({ keyword: $event })"
      @open-filters="showFilterModal = true"
      @remove-filter="onRemoveFilter"
      @clear-filters="search.resetFilters()"
    />

    <!-- Map-first layout: map fills 100%, list overlays on left -->
    <div class="flex-1 relative overflow-hidden">
      <!-- Map fills entire area -->
      <MapContainer @ready="onMapReady" />

      <!-- Property list panel overlaid on the map -->
      <PropertyListPanel
        v-if="!isMobile || mobileView === 'list'"
        :properties="search.properties.value"
        :view-type="viewType"
        :loading="search.loading.value"
        :error="search.error.value"
        :total="search.meta.value.total || 0"
        :current-page="search.page.value"
        :last-page="search.meta.value.last_page || 1"
        :map-points-truncated="search.mapPointsTruncated.value"
        :labels="labels.property_search"
        :collapsed="panelCollapsed"
        @view-change="onViewChange"
        @page-change="search.setPage($event)"
        @highlight="mapbox.highlightMarker($event)"
        @select="onPropertySelect"
        @toggle-collapse="togglePanel"
      />

      <!-- Mobile toggle -->
      <MobileToggle
        v-if="isMobile"
        :active-view="mobileView"
        :labels="labels.property_search"
        @toggle="mobileView = mobileView === 'list' ? 'map' : 'list'"
      />
    </div>

    <!-- Filter modal -->
    <FilterModal
      :show="showFilterModal"
      :labels="labels.property_search"
      :categories="categories"
      :zones="zones"
      :category-id="search.filters.category_id"
      :zone-id="search.filters.zone_id"
      :bedrooms-min="search.filters.bedrooms_min"
      :bathrooms-min="search.filters.bathrooms_min"
      :parking-min="search.filters.parking_min"
      :price-range="search.priceRange.value"
      :price-min="search.filters.price_min"
      :price-max="search.filters.price_max"
      @update:category-id="onFilterChange({ category_id: $event })"
      @update:zone-id="onFilterChange({ zone_id: $event })"
      @update:bedrooms-min="onFilterChange({ bedrooms_min: $event })"
      @update:bathrooms-min="onFilterChange({ bathrooms_min: $event })"
      @update:parking-min="onFilterChange({ parking_min: $event })"
      @update:price-min="onFilterChange({ price_min: $event })"
      @update:price-max="onFilterChange({ price_max: $event })"
      @apply="showFilterModal = false"
      @clear="search.resetFilters(); showFilterModal = false"
      @close="showFilterModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { usePropertySearch } from '@website/composables/usePropertySearch.js';
import { useMapbox, formatPrice } from '@website/composables/useMapbox.js';
import FilterBar from './properties-map/FilterBar.vue';
import PropertyListPanel from './properties-map/PropertyListPanel.vue';
import MapContainer from './properties-map/MapContainer.vue';
import MobileToggle from './properties-map/MobileToggle.vue';
import FilterModal from './properties-map/FilterModal.vue';

const props = defineProps({
  accessToken:    { type: String, required: true },
  searchUrl:      { type: String, required: true },
  categories:     { type: Array,  default: () => [] },
  zones:          { type: Array,  default: () => [] },
  labels:         { type: Object, required: true },
  initialFilters: { type: Object, default: () => ({}) },
});

// --- State ---
const initialIsMobile = window.innerWidth < 768;
const viewType = ref(initialIsMobile ? 'list' : 'grid');
const mobileView = ref('list');
const isMobile = ref(initialIsMobile);
const headerHeight = ref(0);
const showFilterModal = ref(false);
const panelCollapsed = ref(false);

// --- Composables ---
const search = usePropertySearch(props.searchUrl, {
  initialPerPage:  initialIsMobile ? 3 : 4,
  initialFilters:  props.initialFilters,
});

const mapbox = useMapbox(props.accessToken, {
  onBoundsChange: (bounds) => search.setBounds(bounds),
  onMarkerClick: (id, coords) => onMarkerClick(id, coords),
  onClusterClick: (properties, coords) => onClusterClick(properties, coords),
});

// --- Computed ---
const activeFilterPills = computed(() => {
  const labels = props.labels.property_search;
  const pills = [];
  if (search.filters.zone_id) {
    const zone = props.zones.find(z => z.id === search.filters.zone_id);
    if (zone) pills.push({ key: 'zone_id', label: `${labels.zone}: ${zone.name}` });
  }
  if (search.filters.category_id) {
    const cat = props.categories.find(c => c.id === search.filters.category_id);
    if (cat) pills.push({ key: 'category_id', label: `${labels.category}: ${cat.name}` });
  }
  if (search.filters.price_min != null) {
    pills.push({ key: 'price_min', label: `${labels.price_min}: $${search.filters.price_min.toLocaleString()}` });
  }
  if (search.filters.price_max != null) {
    pills.push({ key: 'price_max', label: `${labels.price_max}: $${search.filters.price_max.toLocaleString()}` });
  }
  if (search.filters.bedrooms_min != null) {
    pills.push({ key: 'bedrooms_min', label: labels.bedrooms_short.replace(':count', search.filters.bedrooms_min) });
  }
  if (search.filters.bathrooms_min != null) {
    pills.push({ key: 'bathrooms_min', label: labels.bathrooms_short.replace(':count', search.filters.bathrooms_min) });
  }
  if (search.filters.parking_min != null) {
    pills.push({ key: 'parking_min', label: labels.parking_short.replace(':count', search.filters.parking_min) });
  }
  return pills;
});

const activeFilterCount = computed(() => activeFilterPills.value.length);

function onRemoveFilter(key) {
  const resetValue = key === 'keyword' ? '' : null;
  search.setFilters({ [key]: resetValue });
}

// --- Watch map points to update map ---
watch(search.mapPoints, (points) => {
  mapbox.updateMapPoints(points);
});

// --- Handlers ---
function onMapReady(container) {
  mapbox.initMap(container);
}

function onViewChange(type) {
  viewType.value = type;
  search.setPerPage(type === 'list' ? 3 : 4);
}

function onFilterChange(partial) {
  search.setFilters(partial);
}

function onPropertySelect(property) {
  if (property.lng && property.lat) {
    mapbox.flyTo(property.lng, property.lat);
  }
}

// --- HTML escape helpers for popup ---
function esc(str) {
  const div = document.createElement('div');
  div.textContent = str ?? '';
  return div.innerHTML;
}

function escAttr(str) {
  return esc(str).replace(/"/g, '&quot;');
}

function buildPopupHtml(property) {
  const labels = props.labels.property_search;
  const typeLabel = property.commercialization_type === 'rent' ? labels.for_rent : labels.for_sale;
  const typeBg = property.commercialization_type === 'rent' ? '#DC2626' : '#003DA5';
  const specs = [
    property.bedrooms ? `<i class="ph ph-bed"></i> ${esc(property.bedrooms)}` : '',
    property.bathrooms ? `<i class="ph ph-bathtub"></i> ${esc(property.bathrooms)}` : '',
    property.lot_dimensions ? `<i class="ph ph-ruler"></i> ${esc(property.lot_dimensions)}` : '',
  ].filter(Boolean).join('<span style="margin:0 6px;color:#ccc">·</span>');

  return `
    <a href="${escAttr(property.url)}" style="text-decoration:none;color:inherit;display:block">
      <div style="width:280px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">
        <div style="position:relative">
          <img src="${escAttr(property.thumbnail)}" alt="" style="width:100%;height:160px;object-fit:cover;border-radius:10px;display:block" />
          <span style="position:absolute;top:8px;left:8px;background:${typeBg};color:#fff;font-size:11px;font-weight:700;padding:3px 8px;border-radius:4px">${esc(typeLabel)}</span>
          <span style="position:absolute;bottom:8px;right:8px;background:${typeBg};color:#fff;font-size:13px;font-weight:700;padding:4px 12px;border-radius:10px">${esc(property.price_formatted)}</span>
        </div>
        <div style="padding:10px 12px 8px">
          <div style="font-weight:700;font-size:13px;color:#003DA5;margin-bottom:4px;line-height:1.3">${esc(property.title)}</div>
          ${property.location ? `<div style="font-size:11px;color:#888;margin-bottom:6px"><i class="ph ph-map-pin"></i> ${esc(property.location)}</div>` : ''}
          ${specs ? `<div style="font-size:12px;color:#555;display:flex;align-items:center;gap:2px">${specs}</div>` : ''}
        </div>
      </div>
    </a>
  `;
}

async function onMarkerClick(id, coords) {
  // Try local first (current page results)
  let property = search.properties.value.find(p => p.id === id);

  if (property) {
    mapbox.showPopup(coords, buildPopupHtml(property));
    return;
  }

  // Show loading popup then fetch
  mapbox.showPopup(coords, `
    <div style="width:280px;padding:40px 0;text-align:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">
      <div style="width:24px;height:24px;border:3px solid #e5e7eb;border-top-color:#003DA5;border-radius:50%;margin:0 auto;animation:spin 0.6s linear infinite"></div>
      <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
    </div>
  `);

  property = await search.fetchProperty(id);
  if (property) {
    mapbox.showPopup(coords, buildPopupHtml(property));
  } else {
    mapbox.closePopup();
  }
}

function buildClusterPopupHtml(clusterProps) {
  const labels = props.labels.property_search;
  const items = clusterProps.map((p) => {
    const local = search.properties.value.find(lp => lp.id === p.id);
    const typeLabel = p.type === 'rent' ? labels.for_rent : labels.for_sale;
    const typeBg = p.type === 'rent' ? '#DC2626' : '#003DA5';
    const priceLabel = local?.price_formatted || formatPrice(p.price);
    const title = local ? esc(local.title) : `${esc(typeLabel)} – ${esc(priceLabel)}`;
    const location = local?.location ? `<div style="font-size:11px;color:#888;white-space:nowrap;overflow:hidden;text-overflow:ellipsis"><i class="ph ph-map-pin"></i> ${esc(local.location)}</div>` : '';
    const thumb = local?.thumbnail
      ? `<img src="${escAttr(local.thumbnail)}" alt="" style="width:56px;height:56px;object-fit:cover;border-radius:8px;flex-shrink:0" />`
      : `<div style="width:56px;height:56px;border-radius:8px;flex-shrink:0;background:#f3f4f6;display:flex;align-items:center;justify-content:center"><i class="ph ph-building text-xl" style="color:#9ca3af"></i></div>`;
    const url = local?.url || '#';

    return `
      <a href="${escAttr(url)}" style="text-decoration:none;color:inherit;display:flex;gap:10px;padding:8px;border-radius:10px;transition:background .15s" onmouseover="this.style.background='#f8f9fa'" onmouseout="this.style.background='transparent'">
        ${thumb}
        <div style="min-width:0;flex:1">
          <div style="font-weight:700;font-size:12px;color:#003DA5;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${title}</div>
          ${location}
          <div style="display:flex;align-items:center;gap:6px;margin-top:3px">
            <span style="background:${typeBg};color:#fff;font-size:10px;font-weight:700;padding:2px 6px;border-radius:4px">${esc(typeLabel)}</span>
            <span style="font-size:12px;font-weight:700;color:#333">${esc(priceLabel)}</span>
          </div>
        </div>
      </a>`;
  }).join('');

  return `
    <div style="width:320px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">
      <div style="padding:10px 12px 6px;font-weight:700;font-size:13px;color:#003DA5;border-bottom:1px solid #e5e7eb">
        ${esc(labels.properties_in_area.replace(':count', clusterProps.length))}
      </div>
      <div style="max-height:280px;overflow-y:auto;padding:4px">${items}</div>
    </div>`;
}

function onClusterClick(clusterProperties, coords) {
  mapbox.showPopup(coords, buildClusterPopupHtml(clusterProperties), {
    maxWidth: '360px',
    className: 'cluster-popup',
  });
}

function togglePanel() {
  panelCollapsed.value = !panelCollapsed.value;
  setTimeout(() => mapbox.resize(), 320);
}

// --- Resize handler (consolidated) ---
function onResize() {
  const wasMobile = isMobile.value;
  isMobile.value = window.innerWidth < 768;
  if (isMobile.value && !wasMobile && viewType.value !== 'list') {
    onViewChange('list');
  }
  const header = document.querySelector('header');
  headerHeight.value = header ? header.offsetHeight : 0;
}

measureHeader();
window.addEventListener('resize', onResize);

function measureHeader() {
  const header = document.querySelector('header');
  headerHeight.value = header ? header.offsetHeight : 0;
}

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  mapbox.destroy();
});
</script>
